import { FloorConfigurationState } from "@iko-design-center/shared";
import React, { ReactNode, useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import { useApplication } from "../../../store/application/hooks";
import { ApplicationTypes } from "../../../store/application/store";
import { Home } from "../Home";
import { useFloor } from "../../../store/floor/hooks";
import { useRoutes } from "../../../store/routes/hooks";
import { useQueryParams } from "../../../store/query/floor.hooks";

export const Guard = (props: { children: ReactNode }) => {
  return (
    <>
      <ResetStateGuard>
        <ValidStateGuard>{props.children}</ValidStateGuard>
      </ResetStateGuard>
    </>
  );
};

// This makes sure, that when a user navigates backwards,
// either via the Navbar or via the browser back button,
// the state is reset. This so the user can reselect any options.
const ResetStateGuard = (props: { children: ReactNode }) => {
  const {
    resetConfiguration,
    resetConfigurationToHeating,
    resetConfigurationToInsulation,
    resetConfigurationToScreed,
  } = useFloor();
  const { Routes } = useRoutes() as any;
  const location = useLocation();
  const routeHome = Routes.HOME;
  const routeFloorFloorHeating = Routes.FLOOR_1_FLOOR_HEATING;
  const routeFloorFloorInsulation = Routes.FLOOR_2_FLOOR_INSULATION;
  const routeFloorFloorScreed = Routes.FLOOR_3_FLOOR_SCREED;
  const routeFloorOverview = Routes.FLOOR_4_OVERVIEW;

  useEffect(() => {
    switch (location.pathname) {
      case routeHome:
        resetConfiguration();
        break;
      case routeFloorFloorHeating:
        resetConfigurationToHeating();
        break;
      case routeFloorFloorInsulation:
        resetConfigurationToInsulation();
        break;
      case routeFloorFloorScreed:
        resetConfigurationToScreed();
        break;
      case routeFloorOverview:
        break;
    }
  }, [
    resetConfiguration,
    resetConfigurationToHeating,
    resetConfigurationToInsulation,
    resetConfigurationToScreed,
    location.pathname,
    routeHome,
    routeFloorFloorHeating,
    routeFloorFloorInsulation,
    routeFloorFloorScreed,
    routeFloorOverview,
  ]);

  return <>{props.children}</>;
};

// When a user refreshes the screen on let's say the OverviewPage,
// No state will be present in Redux, thus 'cannot read of undefined's will pop up.
// This prevents that.
const ValidStateGuard = (props: { children: ReactNode }) => {
  const { directLink, setDirectLink, setApplicationType, applicationType } = useApplication();
  const location = useLocation();
  const { redirectBasedOnQueryParams } = useQueryParams()
  const { configurationState } = useFloor();
  const { Routes } = useRoutes() as any;
  const history = useHistory();

  const valid = checkValidStateForRoute(
    applicationType,
    configurationState,
    location.pathname,
    Routes
  );

  useEffect(() => {
    if (!valid) {
      if (!directLink) {
        setDirectLink(true)
        setApplicationType(ApplicationTypes.FLOOR);
        redirectBasedOnQueryParams()
      } else {
        history.push(Routes.HOME);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, setApplicationType, history, setDirectLink, directLink])

  return valid ? <>{props.children}</> : <Home />;
};

function checkValidStateForRoute(
  applicationType: ApplicationTypes | null,
  configurationState: FloorConfigurationState,
  route: string,
  Routes: any
): boolean | undefined {
  const { floorHeating, floorInsulation, floorScreed } = configurationState;

  const applicationTypeValid = applicationType !== null;
  const floorHeatingValid = floorHeating.type !== null;
  const floorInsulationValid = floorInsulation.type !== null;
  const floorScreedValid = floorScreed.type !== null;

  switch (route) {
    case Routes.FLOOR_4_OVERVIEW:
      return floorHeatingValid && floorInsulationValid && floorScreedValid;
    case Routes.FLOOR_3_FLOOR_SCREED:
      return floorHeatingValid && floorInsulationValid;
    case Routes.FLOOR_2_FLOOR_INSULATION:
      return floorHeatingValid;
    case Routes.FLOOR_1_FLOOR_HEATING:
      return applicationTypeValid;
    case Routes.HOME:
      return true;
  }
}
